import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { useCalibration } from "../../contexts/CalibrationContext";

// Set the worker source for pdf.js
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url,
).toString();

export default function SurveyViewer() {
  const {
    isSurveyLoaded,
    surveyType,
    surveyData,
    imageViewerRef,
    pdfViewerRef,
  } = useCalibration();

  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const [, setImageLoaded] = useState(false);

  useEffect(() => {
    if (surveyType === "pdf" && surveyData && pdfViewerRef.current) {
      const loadPdf = async () => {
        try {
          const loadingTask = pdfjsLib.getDocument({ data: surveyData as ArrayBuffer });
          const pdf = await loadingTask.promise;
          pdfDocRef.current = pdf;

          const page = await pdf.getPage(1);
          const scale = 1.5;
          const viewport = page.getViewport({ scale });

          const canvas = pdfViewerRef.current!;
          const context = canvas.getContext("2d")!;
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({
            canvasContext: context,
            viewport: viewport,
            canvas: canvas,
          } as Parameters<typeof page.render>[0]).promise;

          setImageLoaded(true);
        } catch (error) {
          console.error("Error loading PDF:", error);
        }
      };

      loadPdf();
    }
  }, [surveyType, surveyData, pdfViewerRef]);

  useEffect(() => {
    if (surveyType === "image" && surveyData && imageViewerRef.current) {
      imageViewerRef.current.src = surveyData as string;
    }
  }, [surveyType, surveyData, imageViewerRef]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (!isSurveyLoaded) {
    return (
      <div className="text-gray-500 text-sm py-8 text-center">
        Upload a survey to view it here
      </div>
    );
  }

  return (
    <>
      <img
        ref={imageViewerRef}
        alt="Property survey"
        onLoad={handleImageLoad}
        style={{
          display: surveyType === "image" ? "block" : "none",
          maxWidth: "100%",
          height: "auto",
          position: "relative",
          zIndex: 1,
        }}
      />

      <canvas
        ref={pdfViewerRef}
        style={{
          display: surveyType === "pdf" ? "block" : "none",
          maxWidth: "100%",
          height: "auto",
          position: "relative",
          zIndex: 1,
        }}
      />
    </>
  );
}
